import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, MessageCircle, ExternalLink } from "lucide-react";
import { getBillingPixInfo } from "@/lib/api/charges.functions";
import { sendWhatsappSequence } from "@/lib/api/whatsapp.functions";
import { paymentMethodChannel } from "@/lib/payment-methods";
import { buildPaymentWhatsappMessages, createMercadoPagoPixLink } from "@/lib/pix/whatsapp-payment";

export type SaleBillingInput = {
  id: string;
  kind: "uniform" | "product";
  amount: number;
  itemLabel: string;
  payerName: string;
  phone: string | null;
  /** Forma de pagamento escolhida na venda (define o canal da cobrança). */
  paymentMethod?: string | null;
  payerEmail?: string | null;
};

const fmtBRL = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

export function SaleWhatsappBillingModal({
  open, onOpenChange, data,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  data: SaleBillingInput | null;
}) {
  const pixInfoFn = useServerFn(getBillingPixInfo);
  const sendSeqFn = useServerFn(sendWhatsappSequence);

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [phone, setPhone] = useState("");
  const [messages, setMessages] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !data) return;
    setPhone(data.phone || "");
    setLoading(true);
    setMessages([]);
    pixInfoFn()
      .then(async (tc: any) => {
        const amount = Number(data.amount ?? 0);
        const valorFmt = fmtBRL(amount);
        const channel = paymentMethodChannel(data.paymentMethod || "pix_comprovante");
        const introBase =
`Olá ${data.payerName}! Aqui é do ${tc?.name || "centro de treinamento"}.

Você tem uma cobrança em aberto:

📌 *${data.itemLabel}*
💰 Valor: *${valorFmt}*`;

        if (channel === "mercadopago") {
          let mercadoPago: { initPoint?: string | null; brcode?: string | null } | undefined;
          try {
            const res = await createMercadoPagoPixLink({
              training_center_id: tc?.id,
              transaction_amount: amount,
              description: data.itemLabel,
              payer_email: data.payerEmail,
              reference_id: `sale:${data.kind}:${data.id}`,
              method: data.paymentMethod,
            });
            mercadoPago = { initPoint: res.init_point, brcode: res.brcode };
          } catch (e: any) {
            toast.error(`Mercado Pago: ${e?.message || "erro"}`);
          }
          setMessages(buildPaymentWhatsappMessages({
            channel,
            method: data.paymentMethod,
            introMessage: `${introBase}\n\nA próxima mensagem traz o link de pagamento via Mercado Pago.`,
            amount,
            mercadoPago,
          }));
        } else {
          setMessages(buildPaymentWhatsappMessages({
            channel,
            introMessage: `${introBase}\n\nA próxima mensagem traz o *Pix copia-e-cola* já com o valor incluso. Toque e segure para copiar e cole no app do banco.\n\n⚠️ Após realizar o pagamento, envie o comprovante neste mesmo número.`,
            amount,
            pixKey: tc?.pix_key,
            pixBeneficiary: tc?.pix_beneficiary || tc?.name,
            city: tc?.city,
            txid: data.id.replace(/-/g, "").slice(0, 25),
            description: data.itemLabel,
          }));
        }
      })
      .catch((e: any) => {
        toast.error(e?.message || "Erro ao preparar cobrança");
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, data]);

  const updateMsg = (i: number, value: string) =>
    setMessages((arr) => arr.map((m, idx) => (idx === i ? value : m)));

  const sendApi = async () => {
    const to = phone.trim();
    if (!to) { toast.error("Informe o WhatsApp de destino."); return; }
    const toSend = messages.map((m) => m.trim()).filter(Boolean);
    if (!toSend.length) { toast.error("Nenhuma mensagem para enviar."); return; }
    setSending(true);
    try {
      const r: any = await sendSeqFn({ data: {
        recipient: { name: data?.payerName || "cliente", phone: to },
        messages: toSend,
      } });
      if (r.sent > 0) {
        toast.success(`${r.sent} mensagem(ns) enviada(s) via WhatsApp`);
        onOpenChange(false);
      } else {
        toast.error(r.errors?.[0] || "Falha ao enviar");
      }
    } catch (e: any) {
      toast.error(e?.message || "Erro ao enviar cobrança");
    } finally {
      setSending(false);
    }
  };

  const openWeb = () => {
    const raw = phone.replace(/\D/g, "");
    if (!raw) { toast.error("Informe o WhatsApp de destino."); return; }
    const num = raw.length <= 11 ? `55${raw}` : raw;
    const joined = messages.join("\n\n────────\n\n");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(joined)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" /> Enviar cobrança via WhatsApp
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : data ? (
          <div className="space-y-3">
            <div className="text-sm bg-muted/40 p-3 rounded-lg">
              <div><strong>{data.payerName}</strong> — {data.itemLabel} — {fmtBRL(data.amount)}</div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">WhatsApp de destino (DDD + número)</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Ex: 31999998888"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">
                Mensagens ({messages.length}) — serão enviadas em sequência
              </label>
              {messages.map((m, i) => (
                <div key={i} className="rounded-md border bg-background">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 pt-1">
                    Mensagem {i + 1}
                  </div>
                  <Textarea
                    value={m}
                    onChange={(e) => updateMsg(i, e.target.value)}
                    className="min-h-[70px] border-0 focus-visible:ring-0"
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Fechar</Button>
          <Button variant="outline" onClick={openWeb} disabled={sending || loading}>
            <ExternalLink className="h-4 w-4 mr-2" /> WhatsApp Web
          </Button>
          <Button onClick={sendApi} disabled={sending || loading}>
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageCircle className="h-4 w-4 mr-2" />}
            Enviar pela API
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
